<template>
  <div class="page page-question">
    <!-- Top Bar with Logo and Download Button (same as HomeDayReport) -->
    <div class="custom-top-bar">
      <div class="brand-area">
        <img :src="icons.logo" class="brand-logo" alt="logo" />
        <span class="brand-text">Kukukao</span>
      </div>
      <button class="btn-download" @click="handleOpenApp">下载app</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="btn-retry" @click="fetchQuestion">重试</button>
    </div>

    <div v-else>
      <div class="header-container">
        <div class="mascot-wrapper">
          <img :src="apiImages.headerBg" alt="mascot" class="mascot-img" />
          <div class="speech-bubble">
            <div class="bubble-content">在吗？这道法硕题你会做吗？</div>
          </div>
        </div>
      </div>

      <div class="phone">
        <div class="phone-inner">
          <!-- 顶部题干区域 -->
          <div class="question-header">
            <div class="question-tags">
               <div class="badge-level" v-if="questionData.questionType">{{ questionData.questionType }}</div>
            </div>
            <div class="question-title">
              <template v-for="(part, index) in questionStemParts">
                <span v-if="part.isBlank" :key="'blank-' + index" class="question-blank" :class="{ filled: blankAnswers[part.index] }">
                  {{ blankAnswers[part.index] || '____' }}
                </span>
                <span v-else :key="'text-' + index">{{ part.text }}</span>
              </template>
            </div>
            <div class="question-sub" v-if="questionData.kpName">
              ({{ questionData.kpName }})
            </div>
          </div>

          <!-- 选项 -->
          <div class="question-options">
            <div
              v-for="(text, key) in questionData.optionMap"
              :key="key"
              class="option-item"
              :class="{
                active: isFillQuestion ? selectedOptionKeys.indexOf(key) !== -1 : userChoice === key,
                correct: showResult && !isFillQuestion && key === questionData.answer,
                wrong: showResult && !isFillQuestion && userChoice === key && userChoice !== questionData.answer
              }"
              @click="handleSelectOption(key)"
            >
              <!-- 选项字母 A. B. C. -->
              <div class="option-label">{{ key }}.</div>
              <!-- 选项内容 -->
              <div class="option-text">{{ text }}</div>

              <!-- Result Icon -->
              <div class="result-icon" v-if="showResult && key === questionData.answer">✓</div>
              <div class="result-icon wrong" v-if="showResult && userChoice === key && userChoice !== questionData.answer">✕</div>
            </div>
          </div>

          <!-- 规则 TAB + 内容 -->
          <div class="rule-card">
            <div class="tabs">
              <div
                v-for="tab in availableTabs"
                :key="tab.key"
                class="tab"
                :class="{ active: activeTab === tab.key }"
                @click="activeTab = tab.key"
              >
                {{ tab.label }}
              </div>
            </div>

            <div class="rule-body">
               <div v-if="currentTabNodes && currentTabNodes.length > 0">
                 <div
                   v-for="(item, idx) in currentTabNodes"
                   :key="idx"
                   class="rule-item"
                   :style="{ paddingLeft: item.level * 16 + 'px' }"
                 >
                    <div class="rule-dot"></div>
                    <div class="rule-text">{{ item.content }}</div>
                 </div>
               </div>
               <div v-else class="empty-tab">
                 暂无内容
               </div>
            </div>
          </div>

          <div class="bottom-btn" @click="handleOpenApp">
            打开app查看更多题目
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { homeImages } from '../../homeImages'
import headerBg from '@/assets/question/header_bg.svg'
import logo from '@/assets/logo.svg'
import { openApp } from '@/utils/openApp'

export default {
  name: 'HomeLawMasterQuestion',
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      images: homeImages,
      icons: {
        logo
      },
      apiImages: {
        headerBg
      },
      loading: true,
      error: null,
      questionData: {},
      userChoice: null,
      blankAnswers: [],
      selectedOptionKeys: [],
      showResult: false,
      activeTab: 'testPointRestore',
      tabsList: [
        { key: 'testPointRestore', label: '基础解析' },
        { key: 'lawArticleText', label: '法条原文' }
      ]
    }
  },
  computed: {
    questionId() {
      return this.query.id || (this.$route && this.$route.query && this.$route.query.id) || null
    },
    isFillQuestion() {
      return String(this.questionData.questionType || '').indexOf('填空') !== -1
    },
    questionStemParts() {
      const stem = String(this.questionData.questionStem || '')
      if (!this.isFillQuestion || stem.indexOf('%s') === -1) {
        return [{ text: stem, isBlank: false }]
      }

      let blankIndex = 0
      return stem.split(/(%s)/).filter(Boolean).map(part => {
        if (part === '%s') {
          return { text: '', isBlank: true, index: blankIndex++ }
        }
        return { text: part, isBlank: false }
      })
    },
    availableTabs() {
      // Filter tabs that have data? Or show all. Let's show all defined keys
      // The API returns fields like ruleCard, correctExplanation etc.
      return this.tabsList
    },
    currentTabConfig() {
      return this.tabsList.find(tab => tab.key === this.activeTab) || this.tabsList[0]
    },
    currentTabContent() {
      const tab = this.currentTabConfig
      const keys = [tab.key].concat(tab.fallbackKeys || [])
      const key = keys.find(field => this.hasTabData(this.questionData[field]))
      const data = key ? this.questionData[key] : null

      return this.normalizeTabData(data)
    },
    currentTabNodes() {
      return this.flattenTabNodes(this.currentTabContent)
    }
  },
  mounted() {
    this.fetchQuestion()
  },
  methods: {
    async fetchQuestion() {
      if (!this.questionId) {
        this.error = '缺少题目ID参数'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL || ''}/lawMaster/share/getQuestion?id=${this.questionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const result = await response.json()

        if (result.code === 200 && result.data) {
          this.questionData = this.normalizeQuestionData(result.data)
          this.resetAnswerState()
          // 默认展示基础解析
          this.activeTab = 'testPointRestore'
        } else {
          this.error = result.message || '获取题目失败'
        }
      } catch (err) {
        console.error('获取题目失败:', err)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    handleSelectOption(key) {
      if (this.showResult) return // Prevent changing after result shown

      if (this.isFillQuestion) {
        const blankIndex = this.blankAnswers.findIndex(value => value == null)
        if (blankIndex === -1) return

        this.$set(this.blankAnswers, blankIndex, this.questionData.optionMap[key] || key)
        this.$set(this.selectedOptionKeys, blankIndex, key)
        if (blankIndex === this.questionStemParts.filter(part => part.isBlank).length - 1) {
          this.showResult = true
        }
        return
      }

      this.userChoice = key
      this.showResult = true
    },
    resetAnswerState() {
      const stem = String(this.questionData.questionStem || '')
      const blankCount = this.isFillQuestion ? (stem.match(/%s/g) || []).length : 0
      this.blankAnswers = Array(blankCount).fill(null)
      this.selectedOptionKeys = []
      this.userChoice = null
      this.showResult = false
    },
    normalizeQuestionData(data) {
      const options = this.parseJsonValue(data.options)
      const relatedRecites = this.parseJsonValue(data.relatedRecites)

      return Object.assign({}, data, {
        optionMap: options && typeof options === 'object' && !Array.isArray(options) ? options : {},
        testPointRestore: this.parseJsonValue(data.testPointRestore) || '',
        memoryAnchor: this.parseJsonValue(data.memoryAnchor) || '',
        lawArticleText: this.parseJsonValue(data.lawArticleText) || '',
        relatedRecites: this.normalizeRelatedRecites(relatedRecites)
      })
    },
    normalizeRelatedRecites(items) {
      if (!Array.isArray(items)) return []

      return items.reduce((result, item) => {
        if (typeof item === 'string') {
          if (item.trim()) result.push({ content: item })
          return result
        }

        if (!item || typeof item !== 'object') return result

        const reciteNode = {
          content: item.kpName ? String(item.kpName) : '',
          children: []
        }

        const memoryCard = this.parseJsonValue(item.memoryCardContent)
        if (Array.isArray(memoryCard)) {
          reciteNode.children.push(...memoryCard.map(node => {
            return typeof node === 'string' ? { content: node } : node
          }))
        } else if (memoryCard && typeof memoryCard === 'object') {
          reciteNode.children.push(memoryCard)
        } else if (memoryCard != null && String(memoryCard).trim()) {
          reciteNode.children.push({ content: String(memoryCard) })
        }

        if (reciteNode.content || reciteNode.children.length > 0) {
          result.push(reciteNode)
        }
        return result
      }, [])
    },
    parseJsonValue(value) {
      if (value == null || value === '') return value
      if (typeof value !== 'string') return value

      let parsed = value
      for (let i = 0; i < 2; i++) {
        if (typeof parsed !== 'string') return parsed
        try {
          parsed = JSON.parse(parsed)
        } catch (e) {
          return parsed
        }
      }
      return parsed
    },
    hasTabData(data) {
      if (data == null || data === '') return false
      if (Array.isArray(data)) return data.length > 0
      if (typeof data === 'string') return data.trim() !== ''
      if (typeof data === 'object') return Object.keys(data).length > 0
      return true
    },
    normalizeTabData(data) {
      if (!this.hasTabData(data)) return []

      if (Array.isArray(data)) {
        return data.map(item => typeof item === 'string' ? { content: item } : item)
      }

      if (typeof data === 'string') {
        const parsed = this.parseJsonValue(data)
        if (parsed !== data) return this.normalizeTabData(parsed)
        return [{ content: data }]
      }

      if (typeof data === 'object') {
        if (data.content || (Array.isArray(data.children) && data.children.length > 0)) {
          return [data]
        }

        return Object.values(data)
          .filter(this.hasTabData)
          .map(v => {
            if (typeof v === 'string') return { content: v }
            if (typeof v === 'object') return v
            return { content: String(v) }
          })
      }

      return [{ content: String(data) }]
    },
    flattenTabNodes(nodes, level = 0) {
      return nodes.reduce((result, node) => {
        if (!node || typeof node !== 'object') return result

        const content = node.content == null ? '' : String(node.content)
        if (content.trim()) {
          result.push({ content, level })
        }

        if (Array.isArray(node.children) && node.children.length > 0) {
          result.push(...this.flattenTabNodes(node.children, level + 1))
        }

        return result
      }, [])
    },
    handleOpenApp() {
      openApp()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-question {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
  padding-top: calc(56px + env(safe-area-inset-top, 0px)); /* 为固定头部留出空间 */
  position: relative;
  overflow-x: hidden;
}

/* Custom Top Bar：固定顶部，不随页面滚动 */
.custom-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 20px 12px 20px;
  background: #fff;
}

.brand-area {
  display: flex;
  align-items: center;

  .brand-logo {
     width: 32px; height: 32px;
     margin-right: 8px;
  }
  .brand-text {
     font-size: 18px;
     font-weight: 700;
     color: #f15b5b;
  }
}

.btn-download {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 99px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
}

/* 顶部背景区：粉色背景 + 吉祥物 + 气泡 */
.header-container {
  width: 100%;
  height: 240px; /* 根据需要调整高度 */
  background: #FF9F9F; /* 近似设计稿的粉色 */
  background: linear-gradient(180deg, #ffaf9f 0%, #ff9a9e 100%);
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: visible;
}

/* 吉祥物容器 */
.mascot-wrapper {
  position: relative;
  width: 100%;
  max-width: 375px; /* 限制最大宽度，防止在大屏散太开 */
  height: 100%;
  display: flex;
  justify-content: center;
}

.mascot-img {
  width: 200px; /* 调整吉祥物大小 */
  height: auto;
  position: absolute;
  top: 20px; /* 距离顶部的距离 */
  left: 30px; /* 稍微偏左，给气泡留位置 */
  z-index: 1;
}

/* 气泡样式 */
.speech-bubble {
  position: absolute;
  top: 50px;
  right: 20px;
  z-index: 2;
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(2px);
  border-radius: 12px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
  max-width: 160px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);

  /* 气泡小尾巴 */
  &::after {
    content: "";
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-width: 6px 6px 6px 0;
    border-style: solid;
    border-color: transparent rgba(255, 255, 255, 0.6) transparent transparent;
  }

  .bubble-content {
    line-height: 1.4;
  }
}

/* 白色卡片区域，向上覆盖 */
.phone {
  padding: 0 12px;
  margin-top: -68px; /* 向上偏移覆盖 banner */
  position: relative;
  z-index: 10;
}

.phone-inner {
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 16px 30px; /* 底部 Padding 适量即可 */
  box-shadow: 0 4px 16px rgba(0,0,0,0.04);
}

/* 顶部题干 */
.question-header {
  margin-bottom: 20px;
}
.question-tags {
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
}
.badge-level {
  display: inline-block;
  background-color: #FFA000; /* 深橙色 tag */
  color: #fff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  line-height: 1.2;
}

.badge-category {
  display: inline-block;
  background-color: #42A5F5; /* Blue tag */
  color: #fff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  line-height: 1.2;
}

.question-title {
  font-size: 17px;
  font-weight: 700;
  color: #222;
  line-height: 1.5;
  margin-bottom: 6px;
}

.question-blank {
  display: inline-block;
  min-width: 42px;
  margin: 0 4px;
  padding: 0 4px;
  color: #F0615D;
  text-align: center;
  border-bottom: 1px solid #F0615D;
  vertical-align: baseline;

  &.filled {
    min-width: 0;
    font-weight: 700;
  }
}

.question-sub {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* 选项列表 */
.question-options {
  margin-bottom: 24px;
}

.option-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 15px;
  color: #333;
  line-height: 1.6;
  cursor: pointer;
  background: #F8F8F8;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.2s;
  position: relative;

  &.active {
    background: #FFF0F0;
    border-color: #F0615D;
    .option-label { color: #F0615D; }
  }

  &.correct {
    background: #E8F5E9;
    border-color: #4CAF50;
    .option-label { color: #4CAF50; }
  }

  &.wrong {
    background: #FFEBEE;
    border-color: #F44336;
    .option-label { color: #F44336; }
  }
}

.option-label {
  color: #666;
  font-weight: 600;
  margin-right: 12px;
  flex-shrink: 0;
}
.option-text {
  color: #333;
  flex: 1;
}

.result-icon {
  margin-left: 8px;
  font-size: 16px;
  color: #4CAF50;
  font-weight: bold;

  &.wrong {
    color: #F44336;
  }
}

/* 规则卡片部分 */
.rule-card {
  background: #F7F7F7; /* 浅灰底 */
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.tabs {
  display: flex;
  align-items: center;
  justify-content: flex-start; /* 左对齐或者 space-between 看具体设计，图例比较紧凑 */
  margin-bottom: 16px;
  overflow-x: auto;

  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
}

.tab {
  color: #666;
  font-size: 13px; /* Slightly smaller font */
  font-weight: 400;
  cursor: pointer;
  padding: 6px 12px; /* Reduced padding */
  border-radius: 15px;
  flex-shrink: 0;
  margin-right: 8px; /* Reduced margin */
  background: #fff;
  border: 1px solid #E0E0E0;

  &.active {
    background: #F0615D;
    color: #fff;
    border-color: #F0615D;
    font-weight: 500;
  }
}

.rule-body {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}

.rule-item {
  display: flex;
  align-items: baseline;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.rule-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #ffbaba; /* 浅红点 */
  margin-right: 8px;
  transform: translateY(-2px); /* 视觉微调对齐 */
  flex-shrink: 0;
}

.rule-text {
  flex: 1;
  text-align: justify;
}

.empty-tab {
  text-align: center;
  color: #999;
  padding: 20px 0;
}

.bottom-btn {
  width: 100%;
  height: 48px;
  line-height: 48px;
  background: #F0615D;
  color: #fff;
  text-align: center;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  margin-top: 12px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(240, 97, 93, 0.3);

  &:active {
    opacity: 0.9;
    transform: scale(0.98);
  }
}

// Loading State
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #f15b5b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #999;
}

// Error State
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.error-text {
  font-size: 14px;
  color: #f15b5b;
  margin-bottom: 16px;
}

.btn-retry {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}
</style>
